/* eslint-disable camelcase */
// image_url/image_path/sort_order mirror the actual Postgres column names
// in supabase/schema.sql.
import { supabase, isSupabaseConfigured, PRODUCT_IMAGE_BUCKET } from "./supabaseClient";
import { processProductImage } from "./imageUtils";
import first from "../assets/images/Catalog/product_Catalogue_page-0001.jpg";
import second from "../assets/images/Catalog/product_Catalogue_page-0002.jpg";
import third from "../assets/images/Catalog/product_Catalogue_page-0003.jpg";
import fourth from "../assets/images/Catalog/product_Catalogue_page-0004.jpg";
import fifth from "../assets/images/Catalog/product_Catalogue_page-0005.jpg";
import sixth from "../assets/images/Catalog/product_Catalogue_page-0006.jpg";
import seventh from "../assets/images/Catalog/product_Catalogue_page-0007.jpg";
import eighth from "../assets/images/Catalog/product_Catalogue_page-0008.jpg";
import ninth from "../assets/images/Catalog/product_Catalogue_page-0009.jpg";
import tenth from "../assets/images/Catalog/product_Catalogue_page-0010.jpg";
import eleventh from "../assets/images/Catalog/product_Catalogue_page-0011.jpg";
import twelfth from "../assets/images/Catalog/product_Catalogue_page-0012.jpg";
import thirteenth from "../assets/images/Catalog/product_Catalogue_page-0013.jpg";

const SEED_PAGES = [
  first, second, third, fourth, fifth, sixth, seventh,
  eighth, ninth, tenth, eleventh, twelfth, thirteenth,
];

const LOCAL_KEY = "vrfood_catalog_v1";
const isBrowser = typeof window !== "undefined";

function readLocal() {
  if (!isBrowser) return [];
  const raw = window.localStorage.getItem(LOCAL_KEY);
  if (!raw) {
    const seeded = SEED_PAGES.map((image_url, index) => ({
      id: `seed_${index}`,
      image_url,
      sort_order: index,
    }));
    window.localStorage.setItem(LOCAL_KEY, JSON.stringify(seeded));
    return seeded;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return [];
  }
}

function writeLocal(pages) {
  if (!isBrowser) return;
  window.localStorage.setItem(LOCAL_KEY, JSON.stringify(pages));
}

function genId() {
  if (isBrowser && window.crypto?.randomUUID) return window.crypto.randomUUID();
  return `local_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

const localAdapter = {
  async listCatalogPages() {
    return [...readLocal()].sort((a, b) => a.sort_order - b.sort_order);
  },

  async addCatalogPage({ imageFile }) {
    const pages = readLocal();
    const { dataUrl } = await processProductImage(imageFile);
    const nextOrder = pages.length ? Math.max(...pages.map((p) => p.sort_order)) + 1 : 0;
    const page = { id: genId(), image_url: dataUrl, sort_order: nextOrder };
    writeLocal([...pages, page]);
    return page;
  },

  async deleteCatalogPage(id) {
    writeLocal(readLocal().filter((p) => p.id !== id));
  },

  async moveCatalogPage(id, direction) {
    const pages = [...readLocal()].sort((a, b) => a.sort_order - b.sort_order);
    const index = pages.findIndex((p) => p.id === id);
    const swapWith = direction === "up" ? index - 1 : index + 1;
    if (index === -1 || swapWith < 0 || swapWith >= pages.length) return;
    const a = pages[index];
    const b = pages[swapWith];
    [a.sort_order, b.sort_order] = [b.sort_order, a.sort_order];
    writeLocal(pages);
  },
};

async function uploadCatalogImage(imageFile) {
  const { blob } = await processProductImage(imageFile);
  const path = `catalog/${Date.now()}_${Math.random().toString(16).slice(2)}.jpg`;
  const { error: uploadError } = await supabase.storage
    .from(PRODUCT_IMAGE_BUCKET)
    .upload(path, blob, { contentType: "image/jpeg", cacheControl: "31536000" });
  if (uploadError) throw uploadError;
  const { data } = supabase.storage.from(PRODUCT_IMAGE_BUCKET).getPublicUrl(path);
  return { image_url: data.publicUrl, image_path: path };
}

const supabaseAdapter = {
  async listCatalogPages() {
    const { data, error } = await supabase
      .from("catalog_pages")
      .select("*")
      .order("sort_order", { ascending: true });
    if (error) throw error;
    return data;
  },

  async addCatalogPage({ imageFile }) {
    const { data: existing, error: fetchError } = await supabase
      .from("catalog_pages")
      .select("sort_order")
      .order("sort_order", { ascending: false })
      .limit(1);
    if (fetchError) throw fetchError;
    const nextOrder = existing.length ? existing[0].sort_order + 1 : 0;

    const { image_url, image_path } = await uploadCatalogImage(imageFile);
    const { data, error } = await supabase
      .from("catalog_pages")
      .insert([{ image_url, image_path, sort_order: nextOrder }])
      .select()
      .single();
    if (error) throw error;
    return data;
  },

  async deleteCatalogPage(id) {
    const { data: existing, error: fetchError } = await supabase
      .from("catalog_pages")
      .select("image_path")
      .eq("id", id)
      .single();
    if (fetchError) throw fetchError;

    const { error } = await supabase.from("catalog_pages").delete().eq("id", id);
    if (error) throw error;

    if (existing.image_path) {
      try {
        await supabase.storage.from(PRODUCT_IMAGE_BUCKET).remove([existing.image_path]);
      } catch {
        // Best-effort cleanup.
      }
    }
  },

  async moveCatalogPage(id, direction) {
    const { data: pages, error } = await supabase
      .from("catalog_pages")
      .select("id, sort_order")
      .order("sort_order", { ascending: true });
    if (error) throw error;

    const index = pages.findIndex((p) => p.id === id);
    const swapWith = direction === "up" ? index - 1 : index + 1;
    if (index === -1 || swapWith < 0 || swapWith >= pages.length) return;

    const a = pages[index];
    const b = pages[swapWith];
    await Promise.all([
      supabase.from("catalog_pages").update({ sort_order: b.sort_order }).eq("id", a.id),
      supabase.from("catalog_pages").update({ sort_order: a.sort_order }).eq("id", b.id),
    ]);
  },
};

const adapter = isSupabaseConfigured ? supabaseAdapter : localAdapter;

export const { listCatalogPages, addCatalogPage, deleteCatalogPage, moveCatalogPage } = adapter;
